<style lang="scss" src="./application-for-residency.scss"></style>

<template>
  <a-drawer
    :open="open"
    root-class-name="application-for-residency"
    class="application-for-residency"
    @close="onClose"
  >
    <template #title>
      <span class="application-for-residency__header-title">Заявка на резидентство</span>
    </template>

    <template #closeIcon>
      <span class="permission-request-company-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <div class="application-for-residency__section">
      <span class="application-for-residency__section-title">1. Общее</span>

      <a-form
        ref="generalFormRef"
        :model="form"
        :rules="rules"
        layout="vertical"
        class="application-for-residency__form"
      >
        <div class="application-for-residency__section-content">
          <a-form-item
            class="stc-ant-field-skin"
            label="Полное название юр. лица или ИП"
            name="full_name"
            required
          >
            <a-input v-model:value="form.full_name" placeholder="Введите название" />
          </a-form-item>

          <a-form-item
            class="stc-ant-field-skin"
            label="Сфера деятельности компании"
            name="business_area"
            required
          >
            <a-select v-model:value="form.business_area" placeholder="Выберите сферу">
              <a-select-option
                v-for="option in businessAreaOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Кластер" name="cluster" required>
            <a-select v-model:value="form.cluster" placeholder="Выберите кластер">
              <a-select-option
                v-for="option in clusterOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item
            class="stc-ant-field-skin"
            label="Описание проекта"
            name="project_description"
            required
          >
            <a-textarea
              v-model:value="form.project_description"
              class="permission-request-company-drawer__textarea"
              :auto-size="{ minRows: 8, maxRows: 8 }"
              :maxlength="PROJECT_DESCRIPTION_MAX_LENGTH"
              placeholder="Тип проекта, ожидаемые результаты"
              show-count
            />
          </a-form-item>
        </div>
      </a-form>
    </div>

    <a-divider class="application-for-residency__divider" />

    <div class="application-for-residency__section">
      <span class="application-for-residency__section-title">2. Контактное лицо</span>

      <a-form
        ref="contactFormRef"
        :model="form"
        :rules="rules"
        layout="vertical"
        class="application-for-residency__form"
      >
        <div class="application-for-residency__section-content">
          <a-form-item class="stc-ant-field-skin" label="ФИО" name="contactFullName" required>
            <a-input
              v-model:value="form.contactFullName"
              placeholder="Введите фамилию, имя и отчество"
            />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Должность" name="work_position">
            <a-input v-model:value="form.work_position" placeholder="Название должности" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Телефон" name="phone" required>
            <a-input v-model:value="form.phone" placeholder="+7 (___) ___-__-__" />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Почта" name="email" required>
            <a-input v-model:value="form.email" placeholder="example@mail.ru" />
          </a-form-item>
        </div>
      </a-form>
    </div>

    <a-divider class="application-for-residency__divider" />

    <div class="application-for-residency__section">
      <span class="application-for-residency__section-title">3. Документы</span>

      <div class="application-for-residency__section-content">
        <FormFileUpload ref="documentsUploadRef" v-model="documentItems" />
      </div>
    </div>

    <a-divider class="application-for-residency__divider-mobile" />

    <FormPrivacyConsentField v-model:model-value="form.privacy" class="policy-mobile" />

    <template #footer>
      <div class="application-for-residency__footer">
        <FormPrivacyConsentField v-model:model-value="form.privacy" class="policy-footer-mobile" />

        <div class="application-for-residency__buttons-container">
          <button type="button" class="btn btn-outline btn-medium btn-cancel" @click="onClose">
            Отмена
          </button>
          <button
            type="button"
            class="btn btn-primary btn-medium"
            @click="onSubmit"
            :disabled="isSubmitDisabled"
          >
            {{ isSubmitting ? 'Отправка…' : 'Отправить' }}
          </button>
        </div>
      </div>
    </template>
  </a-drawer>

  <ConsultationRequestSuccessModal
    v-model:open="openModal"
    :description="successModalDescription"
  />
</template>

<script lang="ts" setup>
import { computed, nextTick, reactive, ref, watch, onMounted } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import { type FormSubjectOption } from '@/js/components/form/form-subject-field.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { getRecaptchaErrorMessage } from '@/js/utils/getRecaptchaToken';
import FormPrivacyConsentField from '@/js/components/form/form-privacy-consent-field.vue';
import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

interface ResydenceRequestFormMetaItem {
  id: string;
  text: string;
}

interface ResydenceRequestFormMetaResponse {
  status?: string;
  msg?: {
    clusters?: ResydenceRequestFormMetaItem[];
    business_area?: ResydenceRequestFormMetaItem[];
    privacy?: ResydenceRequestFormMetaItem[];
  };
}

interface ResidencyOpenParams {
  cluster?: string;
}

const RESIDENCE_BECOME_ENDPOINT = getApiUrl('/api/form/become-resident/');
const PROJECT_DESCRIPTION_MAX_LENGTH = 512;

function mapMetaToOptions(items: ResydenceRequestFormMetaItem[] | undefined): FormSubjectOption[] {
  return (items ?? []).map((item) => ({ value: item.id, label: item.text }));
}

const open = ref(false);
const openModal = ref(false);
const successModalDescription = ref('');
const isSubmitting = ref(false);
const generalFormRef = ref<FormInstance>();
const contactFormRef = ref<FormInstance>();
const documentsUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);
const documentItems = ref<FileUploadItem[]>([]);

const clusterOptions = ref<FormSubjectOption[]>([]);
const businessAreaOptions = ref<FormSubjectOption[]>([]);
const privacyOptionId = ref<string>('');

const initialFormState = () => ({
  full_name: '',
  business_area: '',
  other_business: '',
  cluster: '',
  project_description: '',
  contactFullName: '',
  phone: '',
  email: '',
  privacy: false,
  work_position: '',
});

const form = reactive(initialFormState());
const isOtherBusinessAreaSelected = computed(() => form.business_area === 'other');

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.full_name) &&
    isNotBlankString(form.business_area) &&
    (!isOtherBusinessAreaSelected.value || isNotBlankString(form.other_business)) &&
    isNotBlankString(form.cluster) &&
    isNotBlankString(form.project_description) &&
    isNotBlankString(form.contactFullName) &&
    isNotBlankString(form.phone) &&
    isNotBlankString(form.email) &&
    form.privacy,
);

const hasValidationErrors = computed(() => !isValidEmail(form.email));
const isSubmitDisabled = computed(
  () => isSubmitting.value || !isRequiredFieldsFilled.value || hasValidationErrors.value,
);

const rules: Record<string, Rule[]> = {
  full_name: [{ required: true, whitespace: true, message: 'Введите полное название' }],
  business_area: [{ required: true, whitespace: true, message: 'Выберите сферу деятельности' }],
  other_business: [{ required: true, whitespace: true, message: 'Укажите сферу деятельности' }],
  cluster: [{ required: true, whitespace: true, message: 'Выберите кластер' }],
  project_description: [
    { required: true, whitespace: true, message: 'Заполните описание проекта' },
  ],
  contactFullName: [{ required: true, whitespace: true, message: 'Введите ФИО' }],
  phone: [{ required: true, whitespace: true, message: 'Введите телефон' }],
  email: [{ required: true, type: 'email', message: 'Введите корректный e-mail' }],
  work_position: [{ required: false, whitespace: true, message: 'Название должности' }],
};

const showModal = () => {
  openModal.value = true;
};

const openDrawer = (params?: ResidencyOpenParams): void => {
  resetFormState();

  if (params?.cluster) {
    const exists = clusterOptions.value.some((option) => option.value === params.cluster);

    if (exists) {
      form.cluster = params.cluster;
    }
  }

  open.value = true;
};

const fetchResydenceRequestFormMeta = async () => {
  try {
    const response = await fetch(RESIDENCE_BECOME_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
    });

    const result = (await response.json()) as ResydenceRequestFormMetaResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(result?.error ?? 'Не удалось загрузить данные формы');
    }

    const privacyList = result.msg?.privacy ?? [];

    clusterOptions.value = mapMetaToOptions(result.msg?.clusters);
    businessAreaOptions.value = mapMetaToOptions(result.msg?.business_area);
    privacyOptionId.value = privacyList[0]?.id ?? '';
  } catch {
    message.error('Не удалось загрузить данные формы');
  }
};

watch(
  () => form.business_area,
  (value) => {
    if (value !== 'other') {
      form.other_business = '';
      nextTick(() => {
        generalFormRef.value?.clearValidate(['other_business']);
      });
    }
  },
);

const resetFormState = () => {
  Object.assign(form, initialFormState());
  documentItems.value = [];
  documentsUploadRef.value?.reset();
  nextTick(() => {
    generalFormRef.value?.clearValidate();
    contactFormRef.value?.clearValidate();
  });
};

const parseContactNameParts = (fullName: string): [string, string, string] => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const lastName = parts[0] ?? '';
  const firstName = parts[1] ?? '';
  const middleName = parts.slice(2).join(' ');
  return [lastName, firstName, middleName];
};

const resolveBusinessAreaValue = (): string => {
  if (form.business_area === 'other') {
    return form.other_business.trim();
  }

  return form.business_area;
};

const onClose = () => {
  open.value = false;
  resetFormState();
};

const onSubmit = async () => {
  if (!generalFormRef.value || !contactFormRef.value) {
    return;
  }

  try {
    await Promise.all([generalFormRef.value.validate(), contactFormRef.value.validate()]);
  } catch {
    return;
  }

  isSubmitting.value = true;
  documentsUploadRef.value?.setSubmittingState(true);

  try {
    const readyFiles = documentsUploadRef.value?.getReadyFiles() ?? [];

    const submitResult = await submitFormWithCaptcha({
      endpoint: RESIDENCE_BECOME_ENDPOINT,
      buildFormData: () => {
        const body = new FormData();
        const [lastName, firstName, middleName] = parseContactNameParts(form.contactFullName);

        body.append('full_name', form.full_name.trim());
        body.append('business_area', resolveBusinessAreaValue());
        body.append('cluster', form.cluster);
        body.append('last_name', lastName);
        body.append('first_name', firstName);
        body.append('middle_name', middleName);
        body.append('email', form.email.trim());
        body.append('phone', form.phone.trim());
        body.append('project_description', form.project_description.trim());

        if (form.work_position) {
          body.append('work_position', form.work_position);
        }

        if (form.privacy && privacyOptionId.value) {
          body.append('privacy', privacyOptionId.value);
        }

        readyFiles.forEach((file) => {
          body.append('documents[]', file);
        });

        return body;
      },
    });

    if (!submitResult.ok || !submitResult.data) {
      if (submitResult.errorCode === 'captcha-not-loaded') {
        message.error(getRecaptchaErrorMessage('captcha-not-loaded'));
        return;
      }

      if (submitResult.errorCode === 'captcha-failed') {
        message.error(getRecaptchaErrorMessage('captcha-failed'));
        return;
      }

      message.error(submitResult.message ?? 'Не удалось отправить заявку');
      return;
    }

    successModalDescription.value = submitResult.data.msg ?? '';
    trackYaGoal('submitApplication');
    onClose();
    showModal();
  } catch {
    message.error('Ошибка соединения');
  } finally {
    isSubmitting.value = false;
    documentsUploadRef.value?.resetSubmittingState();
  }
};

onMounted(() => {
  void fetchResydenceRequestFormMeta();
});

defineExpose({
  open: openDrawer,
});
</script>
